import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Shield, ShieldOff, Trash2, UserPlus, X } from "lucide-react";
import { Modal, Pagination } from "antd";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { changeRole, CheckDuplicateInviteEmail, getTeamDetail, getTeamPendingRequests, handleTeamJoinRequest, IsTeamAdmin, kickMember, sendTeamInvite } from "../funtion";
import { MockTeamMemberItem, TeamMember } from "../type";
import { API_URL } from "env";

type MemberFilter = "all" | "approved" | "pending" | "requested";

const TeamMembersTab: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [filter, setFilter] = useState<MemberFilter>("all");
  const [page, setPage] = useState(1);
  const [myRole, setMyRole] = useState<"Admin" | "Deputy" | "Member" | null>(null);
  const [my, setMy] = useState<TeamMember | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(true);
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingMembers, setPendingMembers] = useState<TeamMember[]>([]);

  const fetchTeamDetail = async () => {
    if (!id) return;
    try {
      const data = await getTeamDetail(Number(id));
      setMembers(data.members);
      setIsAdmin(IsTeamAdmin(data));
      const userStr = localStorage.getItem("user");
      const userId = userStr ? Number(JSON.parse(userStr).id) : null;
      const myMember = userId ? data.members.find((m) => m.user_id === userId) : null;
      setMyRole(myMember ? (myMember.role as "Admin" | "Deputy" | "Member") : null);
      setMy(myMember || null);
    } finally {
      setIsAdminLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamDetail();
  }, [id]);

  async function handleInviteSubmit() {
    if (CheckDuplicateInviteEmail(members, invitedEmail)) {
      toast.error(t("DuplicateInviteEmail"));
    } else {
      try {
        setLoading(true);
        const data = await sendTeamInvite(Number(id), invitedEmail);
        if (data) {
          setIsAddMemberDialogOpen(false);
          setInvitedEmail("");
        }
      } catch (error) {
        toast.error(t("FailedToSendInvitation"));
      } finally {
        setLoading(false);
      }
    }
  }



  const counts = {
    all: members.length,
    approved: members.filter((m) => m.invitation_status === "Accepted").length,
    pending: members.filter((m) => m.invitation_status === "Pending").length,
    requested: members.filter((m) => m.invitation_status === "Requested").length,
  };

  const filteredMembers = members.filter((m) => {
    if (filter === "approved") return m.invitation_status === "Accepted";
    if (filter === "pending") return m.invitation_status === "Pending";
    if (filter === "requested") return m.invitation_status === "Requested";
    return true;
  });

  const pageSize = 10;
  const pagedMembers = filteredMembers.slice((page - 1) * pageSize, page * pageSize);

  type ConfirmActionType = "approve" | "reject" | "setDeputy" | "unsetDeputy" | "removeMember";
  const [confirmAction, setConfirmAction] = useState<{ type: ConfirmActionType; member: TeamMember } | null>(null);

  const handleApprove = async (memberId: number) => {
    await handleTeamJoinRequest(Number(id), memberId, "approve");
    setMembers((prev) => prev.map((m) => (m.user_id === memberId ? { ...m, invitation_status: "Accepted" } : m)));
    toast.success(t("ApproveSuccess"));
    fetchTeamDetail();
  };

  const handleReject = async (memberId: number) => {
    await handleTeamJoinRequest(Number(id), memberId, "reject");
    setMembers((prev) => prev.filter((m) => m.user_id !== memberId));
    toast.success(t("RejectSuccess"));
    fetchTeamDetail();
  };

  const handleSetDeputy = async (memberId: number) => {
    await changeRole(Number(id), memberId, "Deputy");
    setMembers((prev) => prev.map((m) => (m.user_id === memberId ? { ...m, role: "Deputy" } : m)));
    toast.success(t("SetDeputySuccess"));
  };

  const handleUnsetDeputy = async (memberId: number) => {
    await changeRole(Number(id), memberId, "Member");
    setMembers((prev) => prev.map((m) => (m.user_id === memberId ? { ...m, role: "Member" } : m)));
    toast.success(t("UnsetDeputySuccess"));
  };

  const handleRemoveMember = async (memberId: number) => {
    await kickMember(Number(id), memberId);
    setMembers((prev) => prev.filter((m) => m.user_id !== memberId));
    toast.success(t("RemoveMemberSuccess"));
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    switch (confirmAction.type) {
      case "approve":
        handleApprove(confirmAction.member.user_id);
        break;
      case "reject":
        handleReject(confirmAction.member.user_id);
        break;
      case "setDeputy":
        handleSetDeputy(confirmAction.member.user_id);
        break;
      case "unsetDeputy":
        handleUnsetDeputy(confirmAction.member.user_id);
        break;
      case "removeMember":
        handleRemoveMember(confirmAction.member.user_id);
        break;
    }
    setConfirmAction(null);
  };


  const CONFIRM_TITLES: Record<ConfirmActionType, string> = {
    approve: "ConfirmApproveTitle",
    reject: "ConfirmRejectTitle",
    setDeputy: "ConfirmSetDeputyTitle",
    unsetDeputy: "ConfirmUnsetDeputyTitle",
    removeMember: "ConfirmRemoveMemberTitle",
  };

  const CONFIRM_TEXTS: Record<ConfirmActionType, string> = {
    approve: "ConfirmApproveText",
    reject: "ConfirmRejectText",
    setDeputy: "ConfirmSetDeputyText",
    unsetDeputy: "ConfirmUnsetDeputyText",
    removeMember: "ConfirmRemoveMemberText",
  };

  const CONFIRM_BTN_VARIANT: Record<ConfirmActionType, "approve" | "reject"> = {
    approve: "approve",
    reject: "reject",
    setDeputy: "approve",
    unsetDeputy: "reject",
    removeMember: "reject",
  };

  const CONFIRM_BTN_LABEL: Record<ConfirmActionType, string> = {
    approve: "Approve",
    reject: "Reject",
    setDeputy: "SetAsDeputy",
    unsetDeputy: "UnsetDeputy",
    removeMember: "RemoveMember",
  };

  return (
    <div className="team-members-tab">
      <Modal
        title={t("InviteMember")}
        open={isAddMemberDialogOpen}
        onCancel={() => setIsAddMemberDialogOpen(false)}
        footer={null}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-1">
              {t("Email")} *
            </label>
            <input
              name="inviteEmail"
              type="email"
              required
              value={invitedEmail}
              onChange={(e) => setInvitedEmail((e.target as HTMLInputElement).value)}
              placeholder="invitee@example.com"
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          {invitedEmail && CheckDuplicateInviteEmail(members, invitedEmail) && (
            <p className="text-sm text-red-600">{t("DuplicateInviteEmail")}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsAddMemberDialogOpen(false)}
              className="px-4 py-2 border rounded-md text-sm text-gray-600 hover:bg-gray-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              disabled={loading || CheckDuplicateInviteEmail(members, invitedEmail)}
              onClick={handleInviteSubmit}
              className="px-4 py-2 disabled:bg-gray-200 bg-[#e9b460] text-white rounded-md text-sm hover:opacity-90"
            >
              {loading ? t("SendingInvite") : t("Sendinvitation")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        title={confirmAction ? t(CONFIRM_TITLES[confirmAction.type]) : ""}
        open={!!confirmAction}
        onCancel={() => setConfirmAction(null)}
        footer={null}
        centered
        className="team-members-tab__confirm-modal"
      >
        <p className="team-members-tab__confirm-text text-sm mb-4">
          {confirmAction && (typeof confirmAction.member.user_name === "string" ? confirmAction.member.user_name : confirmAction.member.email)}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmAction(null)}
            className="team-members-tab__confirm-btn team-members-tab__confirm-btn--cancel px-4 py-2 rounded-md text-sm"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirmAction}
            className={`team-members-tab__confirm-btn px-4 py-2 rounded-md text-sm text-white ${confirmAction ? `team-members-tab__confirm-btn--${CONFIRM_BTN_VARIANT[confirmAction.type]}` : ""}`}
          >
            {confirmAction && t(CONFIRM_BTN_LABEL[confirmAction.type])}
          </button>
        </div>
      </Modal>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <p className="team-members-tab__title text-lg font-bold m-0">{t("TeamMembersInGroup")}</p>
        {isAdminLoading ? (
          <div className="team-members-tab__add-btn-skeleton h-9 w-36 rounded-lg animate-pulse" />
        ) : (
          isAdmin && (
            <button
              type="button"
              onClick={() => setIsAddMemberDialogOpen(true)}
              className="team-members-tab__add-btn px-4 py-2 rounded-lg text-sm !rounded-[6px] font-medium flex items-center gap-2 transition-colors"
            >
              <UserPlus size={18} />
              {t("InviteMember")}
            </button>
          )
        )}
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <button
          type="button"
          onClick={() => {
            setPage(1);
            setFilter("all");
          }}
          className={`team-members-tab__filter ${filter === "all" ? "team-members-tab__filter--all-active" : ""}`}
        >
          {t("AllMembers")} ({counts.all})
        </button>
        <button
          type="button"
          onClick={() => {
            setPage(1);
            setFilter("approved");
          }}
          className={`team-members-tab__filter ${filter === "approved" ? "team-members-tab__filter--approved-active" : ""}`}
        >
          {t("Approved")} ({counts.approved})
        </button>
        <button
          type="button"
          onClick={() => {
            setPage(1);
            setFilter("requested");
          }}
          className={`team-members-tab__filter ${filter === "requested" ? "team-members-tab__filter--requested-active" : ""}`}
        >
          {t("PendingApproval")} ({counts.requested})
        </button>
      </div>

      <div className="team-members-tab__table-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="team-members-tab__thead-row">
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("TeamMember")}</th>
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("Role")}</th>
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("Status")}</th>
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("JoinDate")}</th>
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-right">{t("Actions")}</th>
              </tr>
            </thead>
            <tbody className="team-members-tab__tbody">
              {pagedMembers.map((member) => (
                <tr key={member.user_id} className="team-members-tab__row transition-colors">
                  <td className="px-6 py-4">
                    {member.user_name && member.user_name.avatar_url ? (
                      <div className="flex items-center gap-2">
                        <img src={member.user_name.avatar_url} alt="avatar" className="w-9 h-9 rounded-full" />
                        <span className="team-members-tab__value text-sm">{member.user_name.fullname || `${member.user_name.first_name} ${member.user_name.last_name}`}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="team-members-tab__avatar w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm">
                          {member.user_name ? member.user_name.fullname?.charAt(0).toUpperCase() : member.email.charAt(0).toUpperCase()}
                        </div>
                        <span className="team-members-tab__name font-semibold text-sm">
                          {member.user_name?.fullname === "N/A" ? member.email : member.user_name?.fullname || member.email}
                          {member.user_id === Number(localStorage.getItem("user_id")) && (
                            <span className="team-members-tab__you-tag"> ({t("You")})</span>
                          )}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {member.role === "Admin" ? (
                      <span className="team-members-tab__role-badge team-members-tab__role-badge--owner">
                        <Shield size={12} className="mr-1" />
                        {t("TeamOwner")}
                      </span>
                    ) : (
                      <span className="team-members-tab__role-badge">
                        {member.role === "Deputy" ? t("Deputy") : t("Member")}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {member.invitation_status === "Accepted" ? (
                      <span className="team-members-tab__status-badge team-members-tab__status-badge--approved">
                        {t("Approved")}
                      </span>
                    ) : (
                      <span className="team-members-tab__status-badge team-members-tab__status-badge--pending">
                        {t("PendingApproval")}
                      </span>
                    )}
                    {/* {member.invitation_status === "Requested" ? (
                      <span className="team-members-tab__status-badge team-members-tab__status-badge--requested">
                        {t("RequestedApproval")} 
                      </span>
                    ) */}
                  </td>
                  <td className="team-members-tab__value px-6 py-4 text-sm">{member.join_date ? new Date(member.join_date).toLocaleDateString() : "-"}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {member.invitation_status === "Requested" && (myRole === "Admin" || myRole === "Deputy") && (
                        <>
                          <button
                            type="button"
                            title={t("Approve")}
                            aria-label={t("Approve")}
                            onClick={() => setConfirmAction({ type: "approve", member })}
                            className="team-members-tab__icon-btn team-members-tab__icon-btn--approve"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            title={t("Reject")}
                            aria-label={t("Reject")}
                            onClick={() => setConfirmAction({ type: "reject", member })}
                            className="team-members-tab__icon-btn team-members-tab__icon-btn--reject"
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                      {
                        member.invitation_status === "Pending" && (
                          <>
                            <div className="flex items-center gap-1">
                              <span className="team-members-tab__status-badge team-members-tab__status-badge--pending">
                                {t("Invited")}
                              </span>
                            </div>
                          </>
                        )}
                      {
                        member.invitation_status === "Accepted" && (
                          (myRole === "Admin" && member.role !== "Admin") ||
                          (myRole === "Deputy" && member.role === "Member")
                        ) && (
                          <>
                            {member.role !== "Deputy" ? (
                              <button
                                type="button"
                                title={t("SetAsDeputy")}
                                aria-label={t("SetAsDeputy")}
                                onClick={() => setConfirmAction({ type: "setDeputy", member })}
                                className="team-members-tab__icon-btn team-members-tab__icon-btn--approve"
                              >
                                <Shield size={16} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                title={t("UnsetDeputy")}
                                aria-label={t("UnsetDeputy")}
                                onClick={() => setConfirmAction({ type: "unsetDeputy", member })}
                                className="team-members-tab__icon-btn team-members-tab__icon-btn--reject"
                              >
                                <ShieldOff size={16} />
                              </button>
                            )}
                            <button
                              type="button"
                              title={t("RemoveMember")}
                              aria-label={t("RemoveMember")}
                              onClick={() => setConfirmAction({ type: "removeMember", member })}
                              className="team-members-tab__icon-btn team-members-tab__icon-btn--reject"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        className="!mt-8 flex justify-end"
        pageSize={pageSize}
        total={filteredMembers.length}
        current={page}
        onChange={(p) => setPage(p)}
        showSizeChanger={false}
      />
    </div>
  );
};

export default TeamMembersTab;
